export type TAIUsageSummary = {
  provider?: string | null;
  totalCost: number | null;
  totalTokens: number | null;
};

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function aggregateUsageMap(usageMap: Record<string, unknown>): TAIUsageSummary | null {
  let totalCost = 0;
  let totalTokens = 0;
  let hasAny = false;
  let provider: string | null = null;

  for (const [modelName, modelUsageValue] of Object.entries(usageMap)) {
    const modelUsage = toRecord(modelUsageValue);
    if (!modelUsage) continue;
    const cost = toNumber(modelUsage.total_cost ?? modelUsage.cost) ?? 0;
    const tokens = toNumber(modelUsage.total_tokens ?? modelUsage.tokens) ?? 0;
    if (!provider && modelName) provider = modelName;
    totalCost += cost;
    totalTokens += tokens;
    hasAny = true;
  }

  if (!hasAny) return null;
  return {
    provider,
    totalCost,
    totalTokens: totalTokens > 0 ? totalTokens : null,
  };
}

export function extractUsageSummary(source: unknown): TAIUsageSummary | null {
  const data = toRecord(source);
  if (!data) return null;

  const normalized = toRecord(data.usage_normalized);
  if (normalized) {
    return {
      provider: typeof normalized.provider === "string" ? normalized.provider : null,
      totalCost: toNumber(normalized.total_cost) ?? toNumber(normalized.cost_usd),
      totalTokens: toNumber(normalized.total_tokens),
    };
  }

  const usageStats = toRecord(data.usage_stats);
  const usage = toRecord(data.usage);
  const llmUsage = toRecord(data.llm_usage);
  const usageMap = usageStats ?? usage ?? llmUsage;
  const aggregated = usageMap ? aggregateUsageMap(usageMap) : null;

  const directCost = toNumber(data.total_cost ?? data.total_llm_cost);
  const directTokens = toNumber(data.total_tokens);
  if (aggregated) {
    return {
      provider: aggregated.provider,
      totalCost: directCost ?? aggregated.totalCost,
      totalTokens: directTokens ?? aggregated.totalTokens,
    };
  }

  if (directCost !== null || directTokens !== null) {
    return {
      provider: null,
      totalCost: directCost,
      totalTokens: directTokens,
    };
  }

  const nestedMetadata = toRecord(data.metadata);
  if (nestedMetadata) {
    return extractUsageSummary(nestedMetadata);
  }

  const companyReadiness = toRecord(data.company_readiness);
  if (companyReadiness) {
    const fromCr = extractUsageSummary(companyReadiness);
    if (fromCr) return fromCr;
  }

  return null;
}

export function formatUsageCost(cost: number | null): string {
  if (cost === null || !Number.isFinite(cost)) return "N/A";
  return `$${cost.toFixed(4)}`;
}

export function formatUsageTokens(tokens: number | null): string {
  if (tokens === null || !Number.isFinite(tokens)) return "N/A";
  return Math.round(tokens).toLocaleString();
}

/** Compact format for System Transparency: e.g. 1_390_000 → "1.39M" */
export function formatUsageTokensCompact(tokens: number | null): string {
  if (tokens === null || !Number.isFinite(tokens)) return "N/A";
  const n = Math.round(tokens);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return n.toLocaleString();
}
